import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useActivateEventTypeMutation, useFormHandleSubmit, useUpdateEventTypeMutation } from 'features/event_type_editor'

export const PRIMARY_ACTIONS_PLACEMENT = {
  header: 'header',
  footer: 'footer',
}

// Shared publish state for the editor header (desktop) and mobile action footer.
export const useEditorPrimaryActions = (eventType, { placement = PRIMARY_ACTIONS_PLACEMENT.header } = {}) => {
  const eventTypeRef = useRef(eventType)
  eventTypeRef.current = eventType
  const { isSubmitting, dirty } = useFormContext()
  // Shares root_form.js's fixedCacheKey (`update-event-type-${eventType.id}`) so this reads the
  // same mutation result without re-triggering it — needed because a failed PATCH with a
  // non-validation error type leaves Formik error-free, so handleSubmit's afterSubmit callback
  // (the "Saved"/"Live" confirmation and the draft-activate call) must also check this directly.
  const [, { error: updateEventTypeError }] = useUpdateEventTypeMutation({ id: eventType.id })
  const handleSubmit = useFormHandleSubmit({ hasMutationError: Boolean(updateEventTypeError) })
  // No `fixedCacheKey` here (unlike more_dropdown.js:162's `activate-event-type-${eventTypeId}`):
  // the old event_type_editor sidebar and this crud editor route can't be mounted for the same
  // event type at once. useCloseOnNavigation (features/event_type_editor/hooks.js) blocks
  // navigation and resets the old sidebar whenever the URL leaves etSidebarPaths
  // (features/event_type_editor/constants.js), and this route ('/event_types/:id/editor',
  // features/event_type_editor_crud/routes.js) isn't one of those paths, so the sidebar's
  // more_dropdown.js instance is always torn down before this component's mounts.
  const [activate, { isLoading: isActivating }] = useActivateEventTypeMutation()
  const [isJustSaved, setIsJustSaved] = useState(false)
  const [isToastVisible, setIsToastVisible] = useState(false)
  const justSavedTimeoutRef = useRef(null)

  const isLive = eventType.active
  const useCompactPublishLabel = placement === PRIMARY_ACTIONS_PLACEMENT.footer

  useEffect(() => {
    if (dirty && isJustSaved) {
      clearTimeout(justSavedTimeoutRef.current)
      setIsJustSaved(false)
    }
  }, [dirty, isJustSaved])

  useEffect(() => () => {
    clearTimeout(justSavedTimeoutRef.current)
  }, [])

  const handleSaveSuccess = () => {
    setIsJustSaved(true)
    setIsToastVisible(true)
    clearTimeout(justSavedTimeoutRef.current)
    justSavedTimeoutRef.current = setTimeout(() => setIsJustSaved(false), 2000)
  }

  const handleToastDismiss = useCallback(() => setIsToastVisible(false), [])

  const handlePublishClick = () => {
    // Guard for the brief window where isJustSaved is true but the primary Button is still
    // mounted (e.g. while loading). The success flash itself is a non-interactive BareButton.
    if (isJustSaved) return undefined

    return isLive ? handleSubmit(handleSaveSuccess) : handleSubmit(() => activate({ eventType: eventTypeRef.current }))
  }

  const getPublishButtonLabel = () => {
    if (isJustSaved) return I18n.t('publisher.event_type_editor_crud.header.saved')
    if (!isLive) return I18n.t('publisher.event_type_editor_crud.header.publish')
    if (dirty) {
      return useCompactPublishLabel
        ? I18n.t('publisher.event_type_editor_crud.header.save')
        : I18n.t('publisher.event_type_editor_crud.header.save_changes')
    }
    return I18n.t('publisher.event_type_editor_crud.header.saved')
  }

  // Keep the primary Button enabled while isJustSaved so a mid-transition loading state
  // doesn't flash CUI's disabled grey. handlePublishClick no-ops until the flash clears.
  const isPublishButtonDisabled = !isJustSaved && (isSubmitting || isActivating || (isLive && !dirty))
  const showJustSavedFlash = isJustSaved && !isSubmitting && !isActivating

  return {
    isLive,
    isSubmitting,
    isActivating,
    isJustSaved,
    isToastVisible,
    isPublishButtonDisabled,
    showJustSavedFlash,
    getPublishButtonLabel,
    handlePublishClick,
    handleToastDismiss,
  }
}
